import React, { useEffect, useState } from 'react';
import {
  Button,
  chakra,
  Checkbox,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  VStack,
  Text,
} from '@chakra-ui/react';

import { axiosWrapper } from '../../hooks/axios';
import Slider from '../../modules/app/components/Slider';
import Poll, { PollField } from './Poll';
import Loader from '../Loader';
import { StemiInput } from '../Input';
interface DemoAskModalProps {
  poll: Poll;
  setIsOpen: (v: boolean) => void;
}

const PollModal: React.FC<DemoAskModalProps> = (props: DemoAskModalProps) => {
  const { poll, setIsOpen } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [values, setValues] = useState<Record<any, any>>({});

  useEffect(() => {
    poll.poll_fields.forEach((pollQuestion, index) => {
      let startValue: any = '';
      if (pollQuestion.type === 'boolean') {
        startValue = null;
      } else if (pollQuestion.type === 'minmax') {
        startValue = null; // Math.round((pollQuestion.max! + pollQuestion.min!) / 2);
      } else if (pollQuestion.type === 'text') {
        startValue = null;
      } else if (pollQuestion.type === 'select') {
        startValue = null;
      }
      values[index] = startValue;
      if (pollQuestion.type === 'minmax') {
        if (pollQuestion.gt) {
          values[index + '-gt'] = null;
        }
        if (pollQuestion.lt) {
          values[index + '-lt'] = null;
        }
      }
    });
    setValues({ ...values });
  }, [poll]);

  const onRequestClose = () => {
    setIsOpen(false);
  };

  const onSubmit = (e: any) => {
    setIsLoading(true);
    axiosWrapper(
      '/api/v1/records/poll/' + poll.id + '/answers/',
      'post',
      values,
    )
      .then((data) => {})
      .finally(() => {
        setIsLoading(false);
        onRequestClose();
      });
  };

  const isFormOk = poll.poll_fields.reduce(
    (acc, p, index) =>
      acc && ((p.required && values[index] !== null) || !p.required),
    true,
  );

  return (
    <Modal isOpen={true} onClose={() => {}} size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text as="h3" textStyle="h3">
            {poll.name}
          </Text>
        </ModalHeader>
        <ModalBody>
          <VStack py={4} justify="start" align="start">
            <Text fontSize="sm">
              In this survey, you’ll express your agreement or disagreement with
              statements using a scale from 1 to 5, where: 1 means “strongly
              disagree” 2 means “disagree” 3 means “neither agree nor disagree”
              4 means “agree” 5 means “strongly agree”
            </Text>
            {poll.poll_fields.map((pollQuestion: any, index: number) => (
              <div key={index} style={{ marginBottom: 20 }}>
                <PollQuestion
                  pollQuestion={pollQuestion}
                  index={index}
                  values={values}
                  setValues={setValues}
                />
              </div>
            ))}
          </VStack>
        </ModalBody>
        <ModalFooter>
          {poll.skip && (
            <Button
              variant="outline"
              colorScheme="blue"
              style={{ marginLeft: '8px' }}
              onClick={onRequestClose}
            >
              Cancel
            </Button>
          )}
          {isLoading ? (
            <Loader />
          ) : (
            <Button
              colorScheme="blue"
              disabled={!isFormOk}
              style={{ marginLeft: '8px' }}
              onClick={onSubmit}
            >
              Save
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PollModal;

interface PollQuestionProps {
  pollQuestion: PollField;
  values: Record<any, any>;
  index: any;
  setValues: (value: any) => void;
}

const PollQuestion = (props: PollQuestionProps) => {
  const { pollQuestion, values, index, setValues } = props;
  return (
    <>
      <div style={{ marginTop: 20 }}>
        <label>
          <Text as="h4" textStyle="h4">
            {!isNaN(index) && (
              <chakra.b fontSize="18px" color="darkblue">
                {parseInt(index) + 1}→{' '}
              </chakra.b>
            )}
            {pollQuestion.question}{' '}
            {pollQuestion.required && values[index] === null && (
              <span style={{ color: '#ff6e6e', fontSize: 14 }}>required</span>
            )}
          </Text>
          {pollQuestion.type === 'boolean' ? (
            <div
              style={{
                flexDirection: 'row',
                display: 'flex',
                justifyContent: 'center',
                padding: 12,
              }}
            >
              <div
                style={{
                  width: 150,
                  flexDirection: 'row',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Checkbox
                  isChecked={values[index] === false}
                  onChange={(e: any) => {
                    values[index] = false;
                    setValues({ ...values });
                  }}
                >
                  <Text as="span" textStyle="h4" ml={1} mr={5}>
                    NO
                  </Text>
                </Checkbox>
                <Checkbox
                  isChecked={values[index] === true}
                  onChange={(e: any) => {
                    values[index] = true;
                    setValues({ ...values });
                  }}
                >
                  <Text as="span" textStyle="h4" ml={1}>
                    YES
                  </Text>
                </Checkbox>
              </div>
            </div>
          ) : pollQuestion.type === 'minmax' ? (
            <Slider
              min={pollQuestion.min}
              max={pollQuestion.max}
              step={1}
              marks={{
                ...Array.from({
                  length: pollQuestion.max! - pollQuestion.min! + 2,
                }).map((_, index) => index + pollQuestion.min! - 1),
              }}
              dots
              defaultValue={values[index]}
              onAfterChange={(value) => {
                values[index] = value;
                setValues({ ...values });
              }}
            />
          ) : pollQuestion.type === 'select' ? (
            <div style={{ width: 'fit-content', marginTop: 20 }}>
              {pollQuestion.options?.map((option, optionIndex) => {
                const selected = values[index] === option;
                return (
                  <HStack
                    key={optionIndex}
                    mt={2}
                    p={2}
                    pr={10}
                    _hover={{
                      cursor: 'pointer',
                      background: '#e7ecf740',
                    }}
                    style={{
                      borderRadius: 4,
                      border: '2px solid #102e8e',
                      ...(selected ? { background: '#e7ecf7' } : {}),
                    }}
                    onClick={() => {
                      values[index] = option;
                      setValues({ ...values });
                    }}
                  >
                    <div
                      style={{
                        border: '2px solid #102e8e',
                        borderRadius: 4,
                        height: 26,
                        width: 26,
                        justifyContent: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: 18,
                        marginRight: 10,
                        background: '#FFF',
                      }}
                    >
                      <chakra.b color="darkblue">
                        {String.fromCharCode(65 + optionIndex)}
                      </chakra.b>
                    </div>
                    <div>
                      <Text as="h4" textStyle="h4" color="darkblue">
                        {option}
                      </Text>
                    </div>
                  </HStack>
                );
              })}
            </div>
          ) : (
            <StemiInput
              style={{ marginTop: 8 }}
              className="input"
              value={values[index]}
              onChange={(e: any) => {
                values[index] = e.target.value;
                setValues({ ...values });
              }}
            />
          )}
        </label>
      </div>
      {pollQuestion.gt && pollQuestion.gt.match < values[index] ? (
        <div style={{ paddingLeft: 20, paddingRight: 20 }}>
          <PollQuestion
            pollQuestion={pollQuestion.gt}
            index={index + '-gt'}
            values={values}
            setValues={setValues}
          />
        </div>
      ) : null}
      {pollQuestion.lt && pollQuestion.lt.match > values[index] ? (
        <div style={{ paddingLeft: 20, paddingRight: 20 }}>
          <PollQuestion
            pollQuestion={pollQuestion.lt}
            index={index + '-lt'}
            values={values}
            setValues={setValues}
          />
        </div>
      ) : null}
    </>
  );
};
